import React, { useState } from 'react';
import { ComponentProp, DesignSystemComponent } from 'designSystem/types';
import * as S from './styles';

interface ComponentPropsEditorProps {
	component: DesignSystemComponent;
	props: Record<string, any>;
	onChange: (props: Record<string, any>) => void;
}

// Separate component for JSON input with error state
const JsonInput = ({
	prop,
	value,
	onChange,
}: {
	prop: ComponentProp;
	value: any;
	onChange: (v: any) => void;
}) => {
	const [jsonError, setJsonError] = useState<string | null>(null);
	const jsonValue =
		value !== undefined && value !== null
			? JSON.stringify(value, null, 2)
			: prop.defaultValue || '{}';

	return (
		<div style={{ width: '100%' }}>
			<textarea
				value={jsonValue}
				onChange={(e) => {
					try {
						const parsed = JSON.parse(e.target.value);
						setJsonError(null);
						onChange(parsed);
					} catch (err) {
						setJsonError('Invalid JSON');
					}
				}}
				placeholder={prop.defaultValue || '{}'}
				style={{
					width: '100%',
					padding: '6px 8px',
					border: `1px solid ${jsonError ? '#ef4444' : '#ddd'}`,
					borderRadius: '4px',
					fontSize: '12px',
					fontFamily: 'ui-monospace, monospace',
					minHeight: '60px',
					resize: 'vertical',
				}}
			/>
			{jsonError && (
				<div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{jsonError}</div>
			)}
		</div>
	);
};

const renderPropInput = (prop: ComponentProp, value: any, onChange: (v: any) => void) => {
	switch (prop.type) {
		case 'string':
			return (
				<input
					type="text"
					value={value || ''}
					onChange={(e) => onChange(e.target.value)}
					placeholder={prop.defaultValue || ''}
					style={{
						width: '100%',
						padding: '6px 8px',
						border: '1px solid #ddd',
						borderRadius: '4px',
						fontSize: '12px',
					}}
				/>
			);
		case 'number':
			return (
				<input
					type="number"
					value={value !== undefined && value !== null ? value : ''}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
					placeholder={prop.defaultValue || '0'}
					style={{
						width: '100%',
						padding: '6px 8px',
						border: '1px solid #ddd',
						borderRadius: '4px',
						fontSize: '12px',
					}}
				/>
			);
		case 'boolean':
			return (
				<input
					type="checkbox"
					checked={value || false}
					onChange={(e) => onChange(e.target.checked)}
				/>
			);
		case 'json':
			// JSON input component - needs its own state for error handling
			return <JsonInput prop={prop} value={value} onChange={onChange} />;
		case 'select':
			return (
				<select
					value={value || prop.defaultValue || ''}
					onChange={(e) => onChange(e.target.value)}
					style={{
						width: '100%',
						padding: '6px 8px',
						border: '1px solid #ddd',
						borderRadius: '4px',
						fontSize: '12px',
					}}
				>
					{prop.options?.map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</select>
			);
		default:
			return (
				<input
					type="text"
					value={value || ''}
					onChange={(e) => onChange(e.target.value)}
					style={{
						width: '100%',
						padding: '6px 8px',
						border: '1px solid #ddd',
						borderRadius: '4px',
						fontSize: '12px',
					}}
				/>
			);
	}
};

export const ComponentPropsEditor = ({ component, props, onChange }: ComponentPropsEditorProps) => {
	const handlePropChange = (propName: string, value: any) => {
		onChange({
			...props,
			[propName]: value,
		});
	};

	const handleReset = () => {
		const defaults: Record<string, any> = {};
		component.props.forEach((prop) => {
			if (prop.defaultValue !== undefined) {
				if (prop.type === 'json') {
					try {
						defaults[prop.name] = JSON.parse(prop.defaultValue);
					} catch {
						defaults[prop.name] = prop.defaultValue;
					}
				} else if (prop.type === 'boolean') {
					defaults[prop.name] = prop.defaultValue === 'true';
				} else if (prop.type === 'number') {
					defaults[prop.name] = Number(prop.defaultValue);
				} else {
					defaults[prop.name] = prop.defaultValue;
				}
			}
		});
		onChange(defaults);
	};

	if (component.props.length === 0) {
		return (
			<S.PropsEditor>
				<S.PropsEditorEmpty>No props available for this component</S.PropsEditorEmpty>
			</S.PropsEditor>
		);
	}

	return (
		<S.PropsEditor>
			<S.PropsEditorHeader>
				<S.PropsEditorTitle>Props</S.PropsEditorTitle>
				<S.PropsEditorReset onClick={handleReset}>Reset to Defaults</S.PropsEditorReset>
			</S.PropsEditorHeader>
			<S.PropsEditorList>
				{component.props.map((prop) => (
					<S.PropsEditorItem key={prop.name}>
						<S.PropsEditorLabel>
							{prop.name}
							{prop.required && <S.PropsEditorRequired>*</S.PropsEditorRequired>}
							{prop.description && (
								<S.PropsEditorDescription>{prop.description}</S.PropsEditorDescription>
							)}
						</S.PropsEditorLabel>
						<S.PropsEditorInput>
							{renderPropInput(prop, props[prop.name], (value) =>
								handlePropChange(prop.name, value)
							)}
						</S.PropsEditorInput>
						{prop.defaultValue !== undefined && (
							<S.PropsEditorDefault>Default: {prop.defaultValue}</S.PropsEditorDefault>
						)}
					</S.PropsEditorItem>
				))}
			</S.PropsEditorList>
		</S.PropsEditor>
	);
};
